// C1 — Dashboard del coordinador.
//
// Refactor visual 2026-05-26 alineado al diseño piloto UPG:
//   - Layout 2 columnas: main (KPIs + tabla líneas + acceso rápido + tendencia)
//     y sidebar derecho (atención + esta semana)
//   - Cadencia toggle (hoy/semana/mes) en header
//   - 4 KPIs específicos con sparkline + delta vs anterior:
//       cumpl. Alpina, cumpl. Meta Interna, producido, paro acumulado
//   - Tabla líneas con sort y drill-down → C6 con ?linea_id=X
//   - Sidebar atencion: 3 tipos (programación nueva, paros recurrentes, líder sin asignar)
//   - Eventos derivados (opción A: lunes/viernes según convención)
//   - Acceso rápido: 4 cards con metadata viva
//   - Tendencia 7 días con LineChart Recharts (toggle Cumplimiento/Producción/Paros)

import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import type { AtencionItem, Cadencia, GlobalPeriodo, LineaTabla } from 'schemas';
import { BarraPeriodo, paramsDePeriodo, usePeriodo, type Preset } from '../../lib/periodo/periodo';
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { EtiquetaValor } from '../../components/charts/value-labels';
import { ConectividadBadge } from '../../components/ui/conectividad-badge';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Icon } from '../../components/ui/icon';
import { InfoHint } from '../../components/ui/info-hint';
import { KpiCard } from '../../components/ui/kpi-card';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Spinner } from '../../components/ui/spinner';
import { SkeletonDashboard } from '../../components/ui/skeleton';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { useAuth } from '../../lib/auth';
import { useCoordinadorDashboard } from '../../lib/api/queries';
import { cn } from '../../lib/utils';
import { semaforoCumplimiento } from '../../lib/semaforo';
import { LineaHoraAHoraDialog } from './LineaHoraAHoraDialog';

// =============================================================================
// Helpers de formato (es-CO)
// =============================================================================

function formatearMiles(n: number): string {
  return new Intl.NumberFormat('es-CO').format(Math.round(n));
}

function formatearMinutos(min: number): string {
  if (min < 60) return `${Math.round(min)}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function formatearFechaCorta(fechaIso: string): string {
  if (!fechaIso || fechaIso.length < 10) return '';
  const parts = fechaIso.slice(0, 10).split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return '';
  const date = new Date(Date.UTC(y, m - 1, d));
  const fmt = new Intl.DateTimeFormat('es-CO', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  return fmt.format(date).replace(/\.|,/g, '').replace(/\s+/g, ' ').trim();
}

// Semáforo de cumplimiento unificado (90/70) — delega en el helper compartido
// para que la MISMA línea se vea igual acá que en C5/C9/LineaTiempo.
function toneCumplimiento(pct: number | null): 'crit' | 'warn' | 'ok' | 'neutral' {
  return semaforoCumplimiento(pct);
}

function toneDelta(pp: number | null): 'ok' | 'crit' | 'neutral' {
  if (pp === null || pp === 0) return 'neutral';
  return pp > 0 ? 'ok' : 'crit';
}

// =============================================================================
// Componente principal
// =============================================================================

// Presets del dashboard (política 2): el selector COMPARTIDO de lib/periodo con
// URL persistente. 'hoy' | 'semana' | 'mes' en curso viajan al API como
// cadencia (el backend resuelve día/semana operativos y el turno nocturno);
// un período pasado o un rango libre viajan como desde/hasta explícitos.
const PRESETS_DASHBOARD: Preset[] = ['hoy', 'semana', 'mes', 'rango'];

type SortLineaKey = 'cumplimiento_pct' | 'gap_unidades' | 'cantidad_real' | 'paros_minutos';
type SortDir = 'asc' | 'desc';

/** Texto del comparativo (DN8: a la misma altura, misma cobertura). */
function etiquetaComparativo(per: ReturnType<typeof usePeriodo>): string {
  if (!per.periodo.isCurrent) return 'vs período anterior';
  if (per.preset === 'hoy') return 'vs ayer';
  if (per.preset === 'semana') return 'vs mismo corte de la sem. anterior';
  if (per.preset === 'mes') return 'vs mismo corte del mes anterior';
  return 'vs período anterior equivalente';
}

export function C1DashboardPage() {
  const { state, signOut } = useAuth();
  const per = usePeriodo(undefined, 'hoy', { url: true });
  const [sortKey, setSortKey] = useState<SortLineaKey>('cumplimiento_pct');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [tendenciaMetric, setTendenciaMetric] = useState<'cumplimiento' | 'produccion' | 'paros'>(
    'cumplimiento',
  );
  // Modal de detalle hora-a-hora: línea seleccionada en la grilla (null = cerrado).
  const [lineaSeleccionada, setLineaSeleccionada] = useState<{
    linea_id: string;
    linea_nombre: string;
  } | null>(null);

  // Filtro por planta desde la URL (?planta=) — así el drill del directivo
  // (D1 → planta) y los deep-links llegan con el contexto explícito. El período
  // también vive en la URL (usePeriodo url:true) → refrescar/volver lo conserva.
  const [searchParams, setSearchParams] = useSearchParams();
  const plantaFiltro = searchParams.get('planta') ?? undefined;
  const setPlanta = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('planta', id);
    else next.delete('planta');
    setSearchParams(next, { replace: true });
  };

  const queryArgs = useMemo<{
    cadencia?: Cadencia;
    desde?: string;
    hasta?: string;
    planta_id?: string;
  }>(() => {
    const enCurso = per.periodo.isCurrent;
    if (enCurso && (per.preset === 'hoy' || per.preset === 'semana' || per.preset === 'mes')) {
      return { cadencia: per.preset, planta_id: plantaFiltro };
    }
    return { desde: per.periodo.desde, hasta: per.periodo.hasta, planta_id: plantaFiltro };
  }, [per.preset, per.periodo.isCurrent, per.periodo.desde, per.periodo.hasta, plantaFiltro]);

  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } =
    useCoordinadorDashboard(queryArgs);

  // Querystring del período para que los drill-downs hereden el contexto.
  const periodoQs = paramsDePeriodo(per.preset, per.anchor, per.rangoSel).toString();

  const navigate = useNavigate();

  // Hooks DE PRIMERO (lección C6) — antes de cualquier early return.
  const lineasSorted = useMemo(() => {
    if (!data?.lineas) return [];
    const sorted = [...data.lineas];
    sorted.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      // Nulls al final SIEMPRE (sin importar dirección).
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      const diff = (va as number) - (vb as number);
      return sortDir === 'asc' ? diff : -diff;
    });
    return sorted;
  }, [data?.lineas, sortKey, sortDir]);

  const tendenciaData = useMemo(() => {
    if (!data?.tendencia_7_dias) return [];
    const { fechas, cumplimiento_pct, produccion, paros_minutos } = data.tendencia_7_dias;
    return fechas.map((fecha, i) => ({
      fecha,
      label: formatearFechaCorta(fecha),
      cumplimiento: cumplimiento_pct[i] ?? null,
      produccion: produccion[i] ?? null,
      paros: paros_minutos[i] ?? null,
    }));
  }, [data?.tendencia_7_dias]);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    // Esqueleto con la forma del dashboard (saludo, KPIs, tabla) en vez de un
    // spinner solo: se ve qué viene y dónde, y no depende de una animación
    // que el sistema pueda apagar.
    return (
      <main className="min-h-screen bg-surface-base p-6 text-fg">
        <SkeletonDashboard filas={8} className="mx-auto max-w-6xl" />
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 bg-surface-base p-6 text-center text-fg">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar el dashboard</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
        <Button onClick={() => refetch()} loading={isFetching}>
          Reintentar
        </Button>
        <Button variant="ghost" size="sm" onClick={() => signOut()}>
          Cerrar sesión
        </Button>
      </main>
    );
  }

  if (data.scope.plantas_asignadas.length === 0) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 bg-surface-base p-6 text-center text-fg">
        <h1 className="text-xl font-medium text-fg">Sin plantas asignadas</h1>
        <p className="text-sm text-fg-secondary">
          Hola <strong className="text-fg">{data.user.nombre}</strong>. No tiene plantas asignadas
          en <strong className="text-fg">{data.tenant.nombre}</strong>.
        </p>
        <p className="text-sm text-fg-tertiary">Contacte al admin del tenant.</p>
        <Button variant="ghost" size="sm" onClick={() => signOut()}>
          Cerrar sesión
        </Button>
      </main>
    );
  }

  const toggleSort = (key: SortLineaKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'cumplimiento_pct' ? 'asc' : 'desc');
    }
  };

  const plantasLabel = data.scope.plantas_asignadas.map((p) => p.nombre).join(' · ');

  const comparativo = etiquetaComparativo(per);
  const gp = data.global_periodo;
  const pend = data.kpis.produccion.pendiente_validacion ?? null;
  const enCurso = data.kpis.paros.en_curso ?? 0;
  const actualizado = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })
    : null;
  const programarPendiente = data.acceso_rapido.programar_proxima_semana.pendiente;

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel={`Coordinador · ${plantasLabel}`}>
      <div className="flex flex-col gap-5">
        {/* ===== Header ===== */}
        <header className="flex flex-col gap-3 border-b border-border pb-4">
          {data.scope.plantas_asignadas.length > 1 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Planta
              </span>
              {/* Selección = NEUTRO (política 3): la marca no marca filtros. */}
              <button
                type="button"
                onClick={() => setPlanta(null)}
                className={cn(
                  'rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
                  !plantaFiltro
                    ? 'border-fg bg-surface-overlay text-fg'
                    : 'border-border text-fg-secondary hover:text-fg',
                )}
              >
                Todas
              </button>
              {data.scope.plantas_asignadas.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPlanta(p.id)}
                  className={cn(
                    'rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
                    plantaFiltro === p.id
                      ? 'border-fg bg-surface-overlay text-fg'
                      : 'border-border text-fg-secondary hover:text-fg',
                  )}
                >
                  {p.nombre}
                </button>
              ))}
            </div>
          ) : null}
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="font-serif text-3xl font-medium leading-none text-fg">
                Hola {data.narrativa.saludo}
              </h1>
              <p className="mt-2 text-[12.5px] text-fg-secondary">
                {plantaFiltro
                  ? (data.scope.plantas_asignadas.find((p) => p.id === plantaFiltro)?.nombre ??
                    plantasLabel)
                  : data.scope.plantas_asignadas.length > 1
                    ? `Todas las plantas (${data.scope.plantas_asignadas.length})`
                    : (data.narrativa.planta_principal ?? plantasLabel)}
                {data.narrativa.turno_label ? ` · ${data.narrativa.turno_label}` : ''} · Hoy lleva{' '}
                <span className="tabular-nums text-fg">
                  {formatearMiles(data.narrativa.cantidad_hoy)}
                </span>{' '}
                paq{' '}
                {data.narrativa.cumpl_hoy_pct !== null ? (
                  <>
                    ·{' '}
                    <span
                      className={cn(
                        'font-medium tabular-nums',
                        toneCumplimiento(data.narrativa.cumpl_hoy_pct) === 'ok'
                          ? 'text-ok'
                          : toneCumplimiento(data.narrativa.cumpl_hoy_pct) === 'warn'
                            ? 'text-warn'
                            : toneCumplimiento(data.narrativa.cumpl_hoy_pct) === 'crit'
                              ? 'text-crit'
                              : 'text-fg-secondary',
                      )}
                    >
                      {data.narrativa.cumpl_hoy_pct.toFixed(0)}%
                    </span>{' '}
                    del programa
                  </>
                ) : null}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              {/* LA acción primaria de la vista (marca) — solo cuando hay algo que hacer. */}
              {programarPendiente ? (
                <Button onClick={() => navigate('/coordinador/programacion-lideres')}>
                  Programar la semana →
                </Button>
              ) : null}
              {actualizado ? (
                <span className="text-[11px] text-fg-tertiary">
                  {isFetching ? 'Actualizando…' : `Actualizado ${actualizado}`}
                </span>
              ) : null}
            </div>
          </div>
          {/* Barra de contexto compartida (política 2): presets + rango resuelto
              + "parcial · N de M días" — el mismo selector en las 3 vistas. */}
          <div className="flex flex-wrap items-center gap-2">
            <BarraPeriodo ctrl={per} presets={PRESETS_DASHBOARD} />
          </div>
        </header>

        {/* ===== 4 KPIs ===== */}
        <Card>
          <CardContent className="grid grid-cols-1 gap-4 p-5 pt-5 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              fitValue
              label={per.preset === 'hoy' && per.periodo.isCurrent ? 'Producido hoy' : 'Producido'}
              hint={
                <>
                  Producción CAPTURADA del período (no invalidada). Lo que aún no validó supervisión
                  se declara al lado — el informe gerencial usa solo lo validado.
                </>
              }
              value={pend === null ? '—' : formatearMiles(data.kpis.produccion.cantidad)}
              valueSuffix={pend === null ? '' : 'paq'}
              sparkline={{
                puntos: data.kpis.produccion.sparkline,
                tone: 'neutral',
              }}
              sub={
                pend === null ? (
                  // Sin capturas ≠ cero producción (política 2.3 / diccionario A1).
                  'sin capturas en el período'
                ) : pend.unidades > 0 ? (
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    capturado
                    <Badge tone="warn">{pend.pct.toFixed(1)}% pendiente de validar</Badge>
                  </span>
                ) : (
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    capturado
                    <Badge tone="ok">todo validado</Badge>
                  </span>
                )
              }
            />
            <KpiCard
              fitValue
              label="Cumplimiento de meta"
              value={
                data.kpis.cumpl_meta_interna.valor_pct !== null
                  ? data.kpis.cumpl_meta_interna.valor_pct.toFixed(0)
                  : '—'
              }
              valueSuffix={data.kpis.cumpl_meta_interna.valor_pct !== null ? '%' : ''}
              sparkline={{
                puntos: data.kpis.cumpl_meta_interna.sparkline,
                tone: toneCumplimiento(data.kpis.cumpl_meta_interna.valor_pct),
              }}
              trend={
                data.kpis.cumpl_meta_interna.delta_pp !== null
                  ? {
                      direction:
                        data.kpis.cumpl_meta_interna.delta_pp > 0
                          ? 'up'
                          : data.kpis.cumpl_meta_interna.delta_pp < 0
                            ? 'down'
                            : 'flat',
                      text: `${data.kpis.cumpl_meta_interna.delta_pp >= 0 ? '+' : ''}${data.kpis.cumpl_meta_interna.delta_pp.toFixed(1)} pp ${comparativo}`,
                      tone: toneDelta(data.kpis.cumpl_meta_interna.delta_pp),
                    }
                  : undefined
              }
              sub={
                data.kpis.cumpl_meta_interna.valor_pct === null
                  ? 'sin capturas con capacidad configurada'
                  : data.kpis.cumpl_meta_interna.delta_pp === null
                    ? `vs capacidad instalada · sin comparación disponible`
                    : undefined
              }
              hint="Producido / capacidad instalada de los bloques capturados (capacidad estándar × horas). Semáforo 90 / 70."
              divider
            />
            <KpiCard
              fitValue
              label="Cumplimiento de programa"
              value={
                data.kpis.cumpl_programa_cliente.valor_pct !== null
                  ? data.kpis.cumpl_programa_cliente.valor_pct.toFixed(0)
                  : '—'
              }
              valueSuffix={data.kpis.cumpl_programa_cliente.valor_pct !== null ? '%' : ''}
              sparkline={{
                puntos: data.kpis.cumpl_programa_cliente.sparkline,
                tone: toneCumplimiento(data.kpis.cumpl_programa_cliente.valor_pct),
              }}
              trend={
                data.kpis.cumpl_programa_cliente.delta_pp !== null
                  ? {
                      direction:
                        data.kpis.cumpl_programa_cliente.delta_pp > 0
                          ? 'up'
                          : data.kpis.cumpl_programa_cliente.delta_pp < 0
                            ? 'down'
                            : 'flat',
                      text: `${data.kpis.cumpl_programa_cliente.delta_pp >= 0 ? '+' : ''}${data.kpis.cumpl_programa_cliente.delta_pp.toFixed(1)} pp ${comparativo}`,
                      tone: toneDelta(data.kpis.cumpl_programa_cliente.delta_pp),
                    }
                  : undefined
              }
              sub={
                data.kpis.cumpl_programa_cliente.valor_pct === null
                  ? gp.programado_total > 0
                    ? 'vs programa del cliente · sin comparación disponible'
                    : 'sin programa cargado en el período'
                  : data.kpis.cumpl_programa_cliente.delta_pp === null
                    ? 'vs programa del cliente · sin comparación disponible'
                    : undefined
              }
              hint="Producido de los SKU programados / programa vigente del cliente, reconciliado por área. Lo producido sin programa se reporta aparte como fuera de plan."
              divider
            />
            <KpiCard
              fitValue
              label="Paro que afecta disponibilidad"
              hint="Solo paros CERRADOS cuya categoría afecta la línea (excluye recesos, medias y otros paros programados que no afectan). Los paros en curso se muestran aparte y no suman."
              value={formatearMinutos(data.kpis.paros.minutos_total)}
              sparkline={{
                puntos: data.kpis.paros.sparkline,
                // La tendencia no es un juicio: sin semáforo por el solo hecho de existir.
                tone: 'neutral',
              }}
              sub={
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  {`${data.kpis.paros.eventos_count} evento${data.kpis.paros.eventos_count === 1 ? '' : 's'} cerrado${data.kpis.paros.eventos_count === 1 ? '' : 's'}`}
                  {enCurso > 0 ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full border border-dashed border-warn px-2 py-px text-[10.5px] font-semibold text-warn"
                      title="Paros abiertos ahora: se cuentan aparte hasta que cierren (DN1)"
                    >
                      <span className="size-1.5 animate-pulse rounded-full bg-warn" />
                      {enCurso} en curso — no suma aún
                    </span>
                  ) : null}
                </span>
              }
              divider
            />
          </CardContent>
        </Card>

        {/* ===== Global del período (la métrica confiable) ===== */}
        <GlobalPeriodoCard gp={gp} />

        {/* ===== Indicador: producido sin programa (desalineación) =====
            Solo cuando HAY programa y parte de la producción quedó fuera de él.
            Sin programa cargado no es una desalineación — el KPI ya lo declara
            (política 3.2: una alarma nace de una regla, no de un color). */}
        {gp.programado_total > 0 && gp.producido_fuera_plan > 0 ? (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-warn/30 bg-warn/10 px-4 py-2.5 text-[12.5px] text-warn">
            <Icon name="alert" size={15} stroke={2} />
            <span className="font-medium tabular-nums">
              {formatearMiles(gp.producido_fuera_plan)} paq producidos sin programa
            </span>
            <InfoHint label="Producido sin programa">
              Producción en líneas/SKU que no están en el plan Alpina del período (posible adelanto
              de otros días o SKU no cargado en Programar producción).
            </InfoHint>
          </div>
        ) : null}

        {/* ===== Layout 2 columnas ===== */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* MAIN — col-span-2 */}
          <div className="flex flex-col gap-5 lg:col-span-2">
            {/* Tabla líneas */}
            <LineasTablaSection
              lineas={lineasSorted}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={toggleSort}
              onSelectLinea={(l) =>
                setLineaSeleccionada({ linea_id: l.linea_id, linea_nombre: l.linea_nombre })
              }
              enlaceAnalisis={`/coordinador/analisis-paros?${periodoQs}`}
            />

            {/* Acceso rápido */}
            <AccesoRapidoSection data={data.acceso_rapido} periodoQs={periodoQs} />
          </div>

          {/* SIDEBAR — col-span-1 */}
          <div className="flex flex-col">
            <AtencionSidebar items={data.atencion} />
          </div>
        </div>

        {/* Tendencia 7 días — fila completa (full-width) */}
        <TendenciaSection
          data={tendenciaData}
          metric={tendenciaMetric}
          onMetricChange={setTendenciaMetric}
        />

        <footer className="text-center text-[11px] text-fg-tertiary">
          {data.tenant.nombre} · producción capturada (lo pendiente de validar se declara) · se
          actualiza cada 30 s
        </footer>
      </div>

      {/* Modal de detalle hora-a-hora (drill-down desde la grilla de líneas) */}
      <LineaHoraAHoraDialog
        linea={lineaSeleccionada}
        turnoId={data.turno_actual_id}
        fecha={data.fecha_hoy}
        onClose={() => setLineaSeleccionada(null)}
      />
    </RoleLayout>
  );
}

// =============================================================================
// GlobalPeriodoCard — la métrica CONFIABLE: producido total vs plan completo
// del período (fuente real). Separa dentro/fuera de plan. No prorratea al día.
// =============================================================================

function GlobalPeriodoCard({ gp }: { gp: GlobalPeriodo }) {
  // Plegado por defecto: el coordinador no necesita verlo todo el tiempo,
  // pero el % de avance queda visible en el header como pulso rápido.
  const [abierto, setAbierto] = useState(false);
  const pct = gp.avance_pct;
  // Cap visual de la barra a 100% (avance puede superar el plan si hay adelantos).
  const barWidth = pct === null ? 0 : Math.min(pct, 100);

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-5">
        <div
          role="button"
          tabIndex={0}
          aria-expanded={abierto}
          onClick={() => setAbierto((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setAbierto((v) => !v);
            }
          }}
          className="flex cursor-pointer flex-wrap items-center justify-between gap-2 -m-1 rounded p-1 hover:bg-surface-overlay/40"
        >
          <h2 className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Global del período
            <span onClick={(e) => e.stopPropagation()}>
              <InfoHint label="Global del período">
                Comparación global: todo lo producido hasta hoy contra el plan COMPLETO del período.
                Perdona adelantos entre días — no está prorrateado al día transcurrido.
              </InfoHint>
            </span>
          </h2>
          <span className="inline-flex items-center gap-2">
            <span className="text-[13px] tabular-nums text-fg-secondary">
              {pct !== null ? `${pct.toFixed(0)}% del plan` : 'sin plan'}
            </span>
            <Icon
              name={abierto ? 'chevron-up' : 'chevron-down'}
              size={14}
              className="text-fg-tertiary"
            />
          </span>
        </div>

        {!abierto ? null : (
          <>
            <span className="text-[11px] tabular-nums text-fg-tertiary">
              {formatearFechaCorta(gp.periodo_desde)}–{formatearFechaCorta(gp.periodo_hasta)}
            </span>

            {/* Avance del plan: % + barra (cap 100%) + producido/programado */}
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                  Avance del plan
                </span>
                <span
                  className="text-[28px] font-medium leading-none text-fg"
                  style={{ letterSpacing: '-1px', fontVariantNumeric: 'tabular-nums' }}
                >
                  {pct !== null ? `${pct.toFixed(0)}%` : '—'}
                </span>
              </div>
              <span className="h-2 w-full overflow-hidden rounded-full bg-surface-overlay">
                <span
                  className="block h-full rounded-full bg-accent transition-[width]"
                  style={{ width: `${barWidth}%` }}
                />
              </span>
              <p className="text-[12px] tabular-nums text-fg-secondary">
                {formatearMiles(gp.producido_total)} / {formatearMiles(gp.programado_total)} paq
              </p>
            </div>

            {/* Desglose dentro / fuera del plan */}
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-[12px] tabular-nums">
              <span className="text-fg-secondary">
                dentro del plan{' '}
                <span className="font-medium text-fg">{formatearMiles(gp.producido_en_plan)}</span>
              </span>
              <span className={cn(gp.producido_fuera_plan > 0 ? 'text-warn' : 'text-fg-secondary')}>
                fuera del plan{' '}
                <span className="font-medium">{formatearMiles(gp.producido_fuera_plan)}</span>
              </span>
            </div>

            {/* Aclaración */}
            <p className="text-[11px] leading-relaxed text-fg-tertiary">
              Global del período: todo lo producido hasta hoy vs el plan completo del período (
              {formatearFechaCorta(gp.periodo_desde)}–{formatearFechaCorta(gp.periodo_hasta)}). No
              está prorrateado al día transcurrido.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// LineasTablaSection
// =============================================================================

function LineasTablaSection({
  lineas,
  sortKey,
  sortDir,
  onSort,
  onSelectLinea,
  enlaceAnalisis,
}: {
  lineas: LineaTabla[];
  sortKey: SortLineaKey;
  sortDir: SortDir;
  onSort: (k: SortLineaKey) => void;
  onSelectLinea: (l: LineaTabla) => void;
  /** Drill al análisis de paros conservando el período (política 2.2). */
  enlaceAnalisis: string;
}) {
  const [query, setQuery] = useState('');
  const [estadoFilter, setEstadoFilter] = useState<'todas' | 'alerta' | 'sin_captura'>('todas');

  const conAlerta = lineas.filter(
    (l) =>
      l.estado_semaforo === 'rojo' ||
      l.estado_semaforo === 'amarillo' ||
      l.estado_semaforo === 'paro',
  ).length;

  const filtradas = useMemo(() => {
    const q = query.trim().toLowerCase();
    return lineas.filter((l) => {
      if (
        q &&
        !l.linea_nombre.toLowerCase().includes(q) &&
        !l.linea_codigo.toLowerCase().includes(q) &&
        !(l.area_nombre ?? '').toLowerCase().includes(q)
      ) {
        return false;
      }
      if (
        estadoFilter === 'alerta' &&
        !(
          l.estado_semaforo === 'rojo' ||
          l.estado_semaforo === 'amarillo' ||
          l.estado_semaforo === 'paro'
        )
      ) {
        return false;
      }
      if (estadoFilter === 'sin_captura' && l.estado_semaforo !== 'sin_captura') return false;
      return true;
    });
  }, [lineas, query, estadoFilter]);

  // Columna de señal solo si alguna línea está conectada a máquina: sin
  // máquinas la tabla queda idéntica.
  const mostrarSenal = lineas.some((l) => l.conectividad);

  return (
    <Card>
      <CardContent className="p-0">
        <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border px-4 py-3">
          <div>
            <h2 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Estado de líneas
            </h2>
            <p className="text-[12px] text-fg-secondary">
              {filtradas.length === lineas.length
                ? `${lineas.length} línea${lineas.length === 1 ? '' : 's'}`
                : `${filtradas.length} de ${lineas.length} líneas`}
              {conAlerta > 0 ? (
                <>
                  {' · '}
                  <span className="text-crit">{conAlerta} con alerta</span>
                  <span className="text-fg-tertiary"> (cumplimiento &lt;90 % o paro activo)</span>
                </>
              ) : null}
            </p>
          </div>
          <Link to={enlaceAnalisis} className="text-[11px] text-fg-secondary hover:text-fg">
            Ver análisis de paros por línea →
          </Link>
        </header>

        {/* Filtros: búsqueda + estado */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar línea o área…"
            className="h-8 min-w-[180px] flex-1 rounded border border-border bg-surface-base px-2.5 text-sm text-fg placeholder:text-fg-tertiary focus:border-accent focus:outline-none"
          />
          <SegmentedControl<'todas' | 'alerta' | 'sin_captura'>
            value={estadoFilter}
            onChange={setEstadoFilter}
            options={[
              { value: 'todas', label: 'Todas' },
              { value: 'alerta', label: 'Con alerta' },
              { value: 'sin_captura', label: 'Sin captura' },
            ]}
          />
        </div>

        {filtradas.length === 0 ? (
          <p className="py-8 text-center text-sm text-fg-tertiary">
            {lineas.length === 0
              ? 'Sin líneas en su scope.'
              : 'Ninguna línea coincide con el filtro.'}
          </p>
        ) : (
          <Table maxHeight="60vh">
            <THead sticky>
              <TR>
                <TH>Línea</TH>
                {mostrarSenal ? <TH>Señal</TH> : null}
                <ThSort
                  active={sortKey === 'cantidad_real'}
                  dir={sortDir}
                  onClick={() => onSort('cantidad_real')}
                  align="right"
                >
                  Producido
                </ThSort>
                <TH align="right">Meta</TH>
                <ThSort
                  active={sortKey === 'cumplimiento_pct'}
                  dir={sortDir}
                  onClick={() => onSort('cumplimiento_pct')}
                  align="right"
                >
                  Cumpl.
                </ThSort>
              </TR>
            </THead>
            <TBody>
              {filtradas.map((l) => (
                <LineaRow
                  key={l.linea_id}
                  l={l}
                  mostrarSenal={mostrarSenal}
                  onSelect={() => onSelectLinea(l)}
                />
              ))}
            </TBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function ThSort({
  active,
  dir,
  onClick,
  align,
  children,
}: {
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  align: 'left' | 'right';
  children: React.ReactNode;
}) {
  return (
    <TH align={align}>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'inline-flex items-center gap-0.5 text-[10px] font-medium uppercase tracking-wider hover:text-fg',
          active ? 'text-fg' : 'text-fg-tertiary',
        )}
      >
        {children}
        {active ? <span className="text-[10px]">{dir === 'asc' ? '↑' : '↓'}</span> : null}
      </button>
    </TH>
  );
}

function LineaRow({
  l,
  mostrarSenal,
  onSelect,
}: {
  l: LineaTabla;
  mostrarSenal: boolean;
  onSelect: () => void;
}) {
  const cumplTone = toneCumplimiento(l.cumplimiento_pct);
  // Borde izquierdo coloreado por estado
  const borderLeftClass =
    l.estado_semaforo === 'paro' || l.estado_semaforo === 'rojo'
      ? 'border-l-2 border-l-crit'
      : l.estado_semaforo === 'amarillo'
        ? 'border-l-2 border-l-warn'
        : l.estado_semaforo === 'verde'
          ? 'border-l-2 border-l-ok'
          : 'border-l-2 border-l-transparent';
  const barColor =
    cumplTone === 'ok'
      ? 'bg-ok'
      : cumplTone === 'warn'
        ? 'bg-warn'
        : cumplTone === 'crit'
          ? 'bg-crit'
          : 'bg-fg-tertiary';
  const cumplTextClass =
    cumplTone === 'ok'
      ? 'text-ok'
      : cumplTone === 'warn'
        ? 'text-warn'
        : cumplTone === 'crit'
          ? 'text-crit'
          : 'text-fg-tertiary';
  const sinCaptura = l.estado_semaforo === 'sin_captura';

  return (
    <TR
      onClick={onSelect}
      className={cn(
        'cursor-pointer transition-colors hover:bg-surface-overlay/50',
        borderLeftClass,
      )}
    >
      <TD>
        <p className="text-sm font-medium text-fg">{l.linea_nombre}</p>
        <p className="text-[11px] uppercase tracking-wide text-fg-tertiary">
          {l.linea_codigo}
          {l.area_nombre ? ` · ${l.area_nombre}` : ''}
        </p>
      </TD>
      {mostrarSenal ? (
        <TD>
          <ConectividadBadge conectividad={l.conectividad} ultimoLatidoAt={l.ultimo_latido_at} />
        </TD>
      ) : null}
      <TD align="right" numeric>
        {sinCaptura ? (
          <Badge tone="warn">Sin captura</Badge>
        ) : (
          <span className="text-sm tabular-nums text-fg">
            {formatearMiles(l.cantidad_real)}{' '}
            <span className="text-[11px] text-fg-tertiary">paq</span>
          </span>
        )}
      </TD>
      <TD align="right" numeric>
        {l.meta_unidades !== null ? (
          <span className="text-sm tabular-nums text-fg-secondary">
            {formatearMiles(l.meta_unidades)}
          </span>
        ) : (
          <span className="text-fg-tertiary">—</span>
        )}
      </TD>
      <TD align="right">
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-16 overflow-hidden rounded-full bg-surface-overlay">
              <span
                className={cn('block h-full rounded-full', barColor)}
                style={{ width: `${Math.min(l.cumplimiento_pct ?? 0, 100)}%` }}
              />
            </span>
            <span
              className={cn('w-10 text-right text-sm font-medium tabular-nums', cumplTextClass)}
            >
              {l.cumplimiento_pct !== null ? `${l.cumplimiento_pct.toFixed(0)}%` : '—'}
            </span>
          </div>
        </div>
      </TD>
    </TR>
  );
}

// =============================================================================
// AtencionSidebar
// =============================================================================

function AtencionSidebar({ items }: { items: AtencionItem[] }) {
  return (
    <Card className="flex h-full flex-col">
      <CardContent className="flex h-full flex-col p-0">
        <header className="flex items-baseline justify-between border-b border-border px-4 py-3">
          <h2 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Requieren su atención
          </h2>
          {items.length > 0 ? (
            <Badge tone="warn">
              {items.length} nuevo{items.length === 1 ? '' : 's'}
            </Badge>
          ) : null}
        </header>
        {items.length === 0 ? (
          <p className="py-6 text-center text-sm text-fg-tertiary">Nada urgente por hoy.</p>
        ) : (
          // min-h-0 + flex-1 + overflow-y-auto → la lista llena la columna y hace
          // scroll interno cuando hay más items de los que entran.
          <ul className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {items.map((it) => (
              <AtencionItemCard key={it.id} item={it} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// Toda alarma nace de una regla identificable (política 3.2): la regla se
// muestra junto al aviso para que el color nunca sea el único mensaje.
const REGLA_ATENCION: Record<
  AtencionItem['tipo'],
  { tone: 'crit' | 'warn' | 'info'; regla: string }
> = {
  paros_recurrentes: {
    tone: 'crit',
    regla: 'Regla: 3 o más paros con la misma causal madre en la misma línea esta semana',
  },
  lider_sin_asignar: {
    tone: 'warn',
    regla: 'Regla: turno programado sin líder a menos de 24 h',
  },
  programacion_alpina_nueva: {
    tone: 'info',
    regla: 'Aviso: nueva versión del programa del cliente pendiente de revisión',
  },
};

function AtencionItemCard({ item }: { item: AtencionItem }) {
  const { tone, regla } = REGLA_ATENCION[item.tipo];
  const borderClass =
    tone === 'crit' ? 'border-l-crit' : tone === 'warn' ? 'border-l-warn' : 'border-l-info';
  const tituloClass = tone === 'crit' ? 'text-crit' : tone === 'warn' ? 'text-warn' : 'text-fg';

  return (
    <li
      className={cn(
        'flex flex-col gap-1 border-b border-border border-l-2 px-4 py-3 last:border-b-0',
        borderClass,
      )}
    >
      <p className={cn('text-sm font-medium', tituloClass)}>{item.titulo}</p>
      <p className="text-[11.5px] text-fg-secondary">{item.detalle}</p>
      <p className="text-[10.5px] text-fg-tertiary">{regla}</p>
      <Link
        to={item.cta_link}
        className="mt-1 text-[11.5px] font-medium text-fg underline-offset-2 hover:underline"
      >
        {item.cta_label}
      </Link>
    </li>
  );
}

// =============================================================================
// AccesoRapidoSection
// =============================================================================

function AccesoRapidoSection({
  data,
  periodoQs,
}: {
  /** Querystring del período actual, para que los accesos hereden el contexto. */
  periodoQs: string;
  data: {
    programar_proxima_semana: {
      semana_iso: string;
      lunes: string;
      domingo: string;
      lineas_total: number;
      pendiente: boolean;
    };
    resumen_semana_anterior: { semana_iso: string; tiene_data: boolean };
    programacion_cliente_ultima: {
      version_numero: number;
      fecha_carga_iso: string | null;
      pendiente_revision: boolean;
    } | null;
    analisis_mes: { mes_label: string; dias_con_data: number };
  };
}) {
  const semProx = data.programar_proxima_semana;
  const semAnt = data.resumen_semana_anterior;
  const cli = data.programacion_cliente_ultima;
  const mes = data.analisis_mes;

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        Acceso rápido
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <AccesoCard
          titulo={`Programar Semana ${semProx.semana_iso.slice(-3)}`}
          badge={semProx.pendiente ? { label: 'PENDIENTE', tone: 'warn' } : null}
          detalle={`${formatearFechaCorta(semProx.lunes)} → ${formatearFechaCorta(semProx.domingo)} · ${semProx.lineas_total} líneas`}
          cta="Abrir programación →"
          link="/coordinador/programacion-lideres"
        />
        <AccesoCard
          titulo={`Resumen Semana ${semAnt.semana_iso.slice(-3)}`}
          badge={null}
          detalle={semAnt.tiene_data ? 'Reunión vie 09:00 · KPIs claves' : 'Sin data registrada'}
          cta="Ver resumen →"
          link={`/coordinador/resumen-semanal?semana_iso=${semAnt.semana_iso}`}
          dim={!semAnt.tiene_data}
        />
        <AccesoCard
          titulo={cli ? `Programación Alpina v${cli.version_numero}` : 'Programación Alpina'}
          badge={cli?.pendiente_revision ? { label: 'NUEVO', tone: 'neutral' } : null}
          detalle={cli ? 'Cargada · pendiente revisión' : 'Sin programación vigente'}
          cta="Revisar diff →"
          link="/coordinador/programacion-produccion"
          dim={!cli}
        />
        <AccesoCard
          titulo={`Análisis de paros`}
          badge={null}
          detalle={`${mes.mes_label} · ${mes.dias_con_data} día${mes.dias_con_data === 1 ? '' : 's'} con data`}
          cta="Abrir análisis →"
          link={`/coordinador/analisis-paros?${periodoQs}`}
        />
      </div>
    </section>
  );
}

function AccesoCard({
  titulo,
  badge,
  detalle,
  cta,
  link,
  dim,
}: {
  titulo: string;
  badge: { label: string; tone: 'warn' | 'neutral' } | null;
  detalle: string;
  cta: string;
  link: string;
  dim?: boolean;
}) {
  return (
    <Card>
      <CardContent className={cn('flex flex-col gap-2 p-4', dim && 'opacity-50')}>
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-medium text-fg">{titulo}</h3>
          {badge ? <Badge tone={badge.tone}>{badge.label}</Badge> : null}
        </div>
        <p className="text-[11.5px] text-fg-tertiary">{detalle}</p>
        {/* Enlaces en tinta neutra: la marca queda para LA acción primaria de la vista. */}
        <Link
          to={link}
          className="text-[11.5px] font-medium text-fg underline-offset-2 hover:underline"
        >
          {cta}
        </Link>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// TendenciaSection — LineChart Recharts con toggle métrica
// =============================================================================

function TendenciaSection({
  data,
  metric,
  onMetricChange,
}: {
  data: Array<{
    fecha: string;
    label: string;
    cumplimiento: number | null;
    produccion: number | null;
    paros: number | null;
  }>;
  metric: 'cumplimiento' | 'produccion' | 'paros';
  onMetricChange: (m: 'cumplimiento' | 'produccion' | 'paros') => void;
}) {
  const datakey = metric;
  const titulo =
    metric === 'cumplimiento'
      ? 'Cumplimiento (%)'
      : metric === 'produccion'
        ? 'Producción (paq)'
        : 'Paros (min)';
  // Una serie de tendencia no es un juicio: color de SERIE (paleta categórica),
  // nunca la marca ni el semáforo (política 3.3).
  const color = 'var(--chart-1)';
  const fmtEtiqueta =
    metric === 'cumplimiento'
      ? (v: number) => `${Math.round(v)}%`
      : (v: number) => Math.round(v).toLocaleString('es-CO');

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-4">
        <header className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Tendencia · últimos 7 días
            </h2>
            <p className="text-[11px] text-fg-secondary">{titulo}</p>
          </div>
          <SegmentedControl<'cumplimiento' | 'produccion' | 'paros'>
            value={metric}
            onChange={onMetricChange}
            options={[
              { value: 'cumplimiento', label: 'Cumplimiento' },
              { value: 'produccion', label: 'Producción' },
              { value: 'paros', label: 'Paros' },
            ]}
          />
        </header>
        <div className="h-56 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 16, right: 16, bottom: 0, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: 'var(--fg-tertiary)' }}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--fg-tertiary)' }}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={false}
                width={42}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--surface-elevated)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  fontSize: 12,
                }}
                labelStyle={{ color: 'var(--fg-secondary)' }}
              />
              <Line
                type="monotone"
                dataKey={datakey}
                stroke={color}
                strokeWidth={2}
                dot={{ r: 3, fill: color }}
                activeDot={{ r: 5 }}
                connectNulls
              >
                <LabelList
                  content={
                    <EtiquetaValor
                      formatter={fmtEtiqueta}
                      total={data.length}
                      dy={-10}
                      fill="var(--fg-secondary)"
                    />
                  }
                />
              </Line>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
